import React, { useState, useCallback } from 'react'
import styled from 'styled-components'
import { Formik, Form, FormikProps, FormikHelpers } from 'formik'
import { useHistory } from 'react-router-dom'
import * as Yup from 'yup'
import moment from 'moment'

import Alert from 'components/Alert'
import { create as createRFP } from 'services/rfp'

import Details from './Details'
import Lanes from './Lanes'
import InviteCarriers from './InviteCarriers'
import Pager from './Pager'
import { Stepper, Step, Container } from './styles'
import { FormValue } from './types'
import { ALLOWED_FILETYPES, SUPPORT_EMAIL } from '../../../constants'

const StepperWrapper = styled.div`
  background: ${({ theme }) => theme.colors.light};
  margin: -40px -157px 40px -157px;
  padding: 40px 0;
  width: 99vw;
`

function getInitialStep() {
  if (process.env.NODE_ENV !== 'development') return 0
  return Number(new URLSearchParams(window.location.search).get('step'))
}

function canAdvance(formikProps: FormikProps<FormValue>) {
  if (formikProps.isSubmitting) return false
  if (!formikProps.values.rfpTitle) return false
  if (formikProps.values.step > 0 && !formikProps.values.lanesFile) return false
  if (Object.values(formikProps.errors).find(Boolean)) return false
  return true
}

function setStep(setFieldValue: (arg0: string, arg1: number) => void) {
  return (step: number) => setFieldValue('step', step)
}

const Schema = Yup.object().shape({
  step: Yup.number(),
  rfpTitle: Yup.string().min(2).max(255).required('This field is required.'),
  bidPricing: Yup.string().required().min(1),
  contractStartDate: Yup.date()
    .required()
    .min(moment().startOf('day').toDate(), 'Must be later than today'),
  contractEndDate: Yup.date().min(
    Yup.ref('contractStartDate'),
    'Must be later than the start date.'
  ),
  lanesFile: Yup.mixed()
    .when('step', (step: number, schema: any) => {
      return step >= 1 ? schema.required('This field is required.') : schema
    })
    .test('fileFormat', 'Unsupported file format.', (value) => {
      if (!value) return true
      if (!ALLOWED_FILETYPES.includes(value.type)) return false
      return true
    }),
  requestDeadline: Yup.date()
    .required()
    .min(moment().toDate(), 'Must be later than now.')
    .max(Yup.ref('contractStartDate'), 'Must be before the contract start'),
  invitedCarriers: Yup.array(),
})

const initialValues: FormValue = {
  step: getInitialStep(),
  rfpTitle: '',
  bidPricing: '',
  contractStartDate: moment().startOf('days').add(1, 'days'),
  contractEndDate: moment().startOf('days').add(2, 'days'),
  lanesFile: null,
  requestDeadline: moment().add(1, 'hour').startOf('hour'),
  invitedCarriers: [],
}

const components = [Details, Lanes, InviteCarriers]

async function submitRFP(values: FormValue) {
  if (!values.lanesFile) throw Error('Missing required lanes file.')

  const formData = new FormData()
  formData.append('name', values.rfpTitle)
  formData.append('state', 'draft')
  formData.append('contract_start', values.contractStartDate.toISOString())
  formData.append('contract_end', values.contractEndDate.toISOString())
  formData.append('request_deadline', values.requestDeadline.toISOString())
  formData.append('file', values.lanesFile, values.lanesFile.name)
  values.invitedCarriers.forEach((carrier) => {
    formData.append(`carrier_invites`, carrier.id.toString())
  })

  const data = await createRFP(formData)
  return data
}

export default function RFPDetail() {
  const history = useHistory()
  const [error, setError] = useState(false)

  const handleSubmit = useCallback(
    async (values: FormValue, helpers: FormikHelpers<FormValue>) => {
      setError(false)
      helpers.setSubmitting(true)
      try {
        await submitRFP(values)
        history.push('/shipper/rfp?state=draft')
      } catch (err) {
        setError(true)
      } finally {
        helpers.setSubmitting(false)
      }
    },
    [history]
  )

  return (
    <Formik
      validationSchema={Schema}
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {(formikProps: FormikProps<FormValue>) => {
        const Component = components[formikProps.values.step]
        const props = { formikProps }
        return (
          <>
            <StepperWrapper>
              <Stepper activeStep={formikProps.values.step}>
                <Step label="Set RFP Details" />
                <Step label="Upload Lanes" />
                <Step label="Invite Carriers" />
              </Stepper>
            </StepperWrapper>
            {error && (
              <Container>
                <Alert type="error">
                  <b>Could not submit RFP.</b>
                  &nbsp;Please reach our support at&nbsp;
                  <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                </Alert>
              </Container>
            )}
            <Form autoComplete="off">
              <Component {...props} />
              <Pager
                step={formikProps.values.step}
                setStep={setStep(formikProps.setFieldValue)}
                canAdvance={canAdvance(formikProps)}
                canGoBack={!formikProps.isSubmitting}
                components={components}
              />
            </Form>
          </>
        )
      }}
    </Formik>
  )
}
